// src/hooks/useClinics.js
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as clinicApi from "../Api/clinicApi";
import getErrorMessage from "./getErrorMssg";

export const useClinics = (options = {}) => {
  const defaultOptions = {
    pageNumber: 1,
    pageSize: 10,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    ...options,
  };

  const queryClient = useQueryClient();

  // Get clinics query - Updated to v5 syntax
  const clinicsQuery = useQuery({
    queryKey: ["clinics", defaultOptions.pageNumber, defaultOptions.pageSize],
    queryFn: () =>
      clinicApi.fetchClinics(
        defaultOptions.pageNumber,
        defaultOptions.pageSize
      ),
    staleTime: defaultOptions.staleTime,
    keepPreviousData: true,
    ...options,
  });

  // Add clinic mutation - Updated to v5 syntax
  const addClinic = useMutation({
    mutationFn: clinicApi.addClinic,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
    },
    onError: (error) => {
      throw new Error(getErrorMessage(error));
    },
  });

  // Update clinic mutation - Updated to v5 syntax
  const updateClinic = useMutation({
    mutationFn: clinicApi.updateClinic,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
    },
    onError: (error) => {
      throw new Error(getErrorMessage(error));
    },
  });

  // Delete clinic mutation - Updated to v5 syntax
  const deleteClinic = useMutation({
    mutationFn: clinicApi.deleteClinic,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
    },
    onError: (error) => {
      throw new Error(getErrorMessage(error));
    },
  });

  return {
    clinicsQuery,
    addClinic,
    updateClinic,
    deleteClinic,
  };
};

// Hook to get a single clinic by ID from cache
export const useClinic = (clinicId) => {
  const { clinicsQuery } = useClinics();

  return {
    clinic: clinicsQuery.data?.clinics?.find(
      (clinic) => clinic.id === clinicId
    ),
    isLoading: clinicsQuery.isLoading,
    isError: clinicsQuery.isError,
    error: clinicsQuery.error,
  };
};
